// src/theme.ts
import type { PaletteOptions, ThemeOptions } from '@mui/material/styles'
import { createTheme } from '@mui/material/styles'

import shadows from '@/src/shadows'
import FONTS from './fonts'
import COLORS from './colors'

// ---------- 3) Fabrique de thème ----------
type MakeThemeArgs = {
  palette: PaletteOptions
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    product: true
    head: true
    qty_selector: true
    general_panel: true
    clear: true
  }
}

// Ajouter des variantes reconnues par TS pour Paper (Card hérite de Paper pour le prop variant)
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    general_panel: true
    internal: true
    variant20: true
    qty_selector: true
  }
}

// Ajouter des variantes reconnues par TS pour Card
declare module '@mui/material/Card' {
  interface CardPropsVariantOverrides {
    clear: true
    internal: true
    variant20: true
  }
}

const makeTheme = ({ palette }: MakeThemeArgs): ThemeOptions => ({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1850, xl: 1950 },
  },

  palette: {
    primary: { ...palette.primary },
    secondary: { contrastText: '#ffffff', ...palette.secondary },
    text: { primary: '#213390', secondary: '#213390', ...palette.text },
    background: { default: '#F6FAFF', paper: COLORS.backgroundLight, ...palette.background },
  },

  // @ts-ignore (si shadows est custom)
  shadows,

  typography: {
    fontFamily: FONTS.primary,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontSize: 12,
  },
  components: {
    MuiCard: {
      defaultProps: {
        elevation: 7,
      },
      variants: [
        {
          props: { variant: 'clear' },
          style: () => ({
            height: '100%',
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            margin: 30,
          }),
        },
        {
          props: { variant: 'internal' },
          style: () => ({
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            margin: 0,
          }),
        },
        {
          props: { variant: 'variant20' },
          style: () => ({
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            margin: 0,
          }),
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 32,
          lineHeight: 1,
          minHeight: 40,
        },
        containedPrimary: {
          color: '#ffffff',
        },
        /*label: {
          letterSpacing: '0.05em',
          paddingTop: 4,
          paddingBottom: 4,
        },*/
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderRadius: 32,
          },
          '& input': {
            borderRadius: 32,
            backgroundColor: '#FFF',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        adornedEnd: {
          paddingRight: 0,
        },
        input: {
          padding: '12px 14px',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          width: '100%',
          padding: 32,
          paddingTop: 32,

          '&:last-child': {
            paddingBottom: 32,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          borderRadius: 32,
          '& label': {
            borderRadius: 32,
            '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
              padding: 4,
              backgroundColor: '#FFF',
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: FONTS.WEIGHT.BOLD,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          lineHeight: 1,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          maxHeight: 30,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontWeight: FONTS.WEIGHT.BOLD,
          fontSize: 21,
        },
        h2: {
          fontWeight: FONTS.WEIGHT.BOLD,
          fontSize: 21,
          textTransform: 'uppercase',
        },
        subtitle1: {
          fontWeight: FONTS.WEIGHT.BOLD,
          fontSize: 17,
          textTransform: 'uppercase',
        },
        subtitle2: {
          fontWeight: FONTS.WEIGHT.BOLD,
          fontSize: 17,
        },
        paragraph: {
          marginBottom: 24,
        },
      },
    },
  },
})

// ---------- 4) Instances de thèmes ----------
const themeStore = createTheme(makeTheme({ palette: COLORS.storeDefault }))

const themeCos = createTheme(makeTheme({ palette: COLORS.cos }))

const themeLesOfficinales = createTheme(makeTheme({ palette: COLORS.lesOfficinales }))

const themeGcapPharma = createTheme(makeTheme({ palette: COLORS.gcapPharma }))

const themeCentraleMediprix = createTheme(makeTheme({ palette: COLORS.centraleMediprix }))

const themePharmacologyStore = createTheme(makeTheme({ palette: COLORS.pharmacologyStore }))

const themeCentraleDpharma = createTheme(makeTheme({ palette: COLORS.centraleDpharma }))

const themeViatris = createTheme(makeTheme({ palette: COLORS.viatris }))

const themeAelia = createTheme(makeTheme({ palette: COLORS.aelia }))

const themeHelloPharmacie = createTheme(makeTheme({ palette: COLORS.helloPharmacie }))

const themeNormandiePharma = createTheme(makeTheme({ palette: COLORS.normandiePharma }))

const themeTrenteSeptDegres = createTheme(makeTheme({ palette: COLORS.trenteSeptDegres }))

const themeOutremer = createTheme(makeTheme({ palette: COLORS.outremer }))

const withTheme = (arg: any) => ('palette' in arg ? arg : arg.theme)

themeOutremer.components = {
  ...(themeOutremer.components ?? {}),
  MuiPaper: {
    defaultProps: { square: false },
    styleOverrides: {
      root: () => ({
        boxShadow: '0px 8px 24px rgba(11, 61, 143, 0.10)',
      }),
    },
    variants: [
      {
        props: { variant: 'general_panel' },
        style: () => ({
          padding: 2,
          boxShadow: '0 12px 20px rgba(11, 61, 143, 0.12)',
          margin: 30,
        }),
      },
      {
        props: { variant: 'internal' },
        style: () => ({
          padding: 12,
          boxShadow: '0 6px 16px rgba(11, 61, 143, 0.08)',
        }),
      },
      {
        props: { variant: 'qty_selector' },
        style: (arg: any) => {
          const theme = withTheme(arg)
          return {
            padding: 12,
            boxShadow: '0 6px 16px rgba(11, 61, 143, 0.08)',
            backgroundColor: theme.palette.background.default,
          }
        },
      },
    ],
  },
  MuiCard: {
    styleOverrides: {
      root: () => ({
        boxShadow: '0px 8px 24px rgba(11, 61, 143, 0.10)',
        '&:hover': {
          boxShadow: '0px 8px 28px rgba(11, 61, 143, 0.18)',
          borderColor: 'rgba(0, 163, 153, 0.25)',
        },
      }),
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 32,
        lineHeight: 1,
        minHeight: 40,
      },
      containedPrimary: {
        background: 'linear-gradient(90deg, #0B3D8F 0%, #1A5CB5 100%)',
        color: '#FFFFFF',
        '&:hover': {
          background: 'linear-gradient(90deg, #072A65 0%, #0B3D8F 100%)',
        },
      },
      containedSecondary: {
        background: 'linear-gradient(90deg, #0052CC 0%, #00A399 100%)',
        color: '#FFFFFF',
        '&:hover': {
          background: 'linear-gradient(90deg, #003A99 0%, #007A73 100%)',
        },
      },
    },
  },
}
themeStore.components = {
  ...(themeStore.components ?? {}),
  MuiPaper: {
    defaultProps: { square: false },
    styleOverrides: {
      root: () => {
        return {
          boxShadow: '0px 8px 24px rgba(0,0,0,0.15)',
        }
      },
    },
    variants: [
      {
        props: { variant: 'general_panel' },
        style: () => {
          return {
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            margin: 30,
          }
        },
      },
      {
        props: { variant: 'internal' },
        style: () => {
          return {
            padding: 12,
            boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
          }
        },
      },
      {
        props: { variant: 'variant20' },
        style: () => {
          return {
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
          }
        },
      },
      {
        props: { variant: 'qty_selector' },
        style: arg => {
          const theme = withTheme(arg)
          return {
            padding: 12,
            boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
            backgroundColor: theme.palette.background.default,
          }
        },
      },
    ],
  },
  MuiCard: {
    styleOverrides: {
      root: () => {
        return {
          boxShadow: '0px 8px 24px rgba(0,0,0,0.15)',
        }
      },
    },
    variants: [
      {
        props: { variant: 'clear' },
        style: () => {
          return {
            height: '100%',
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            margin: 30,
          }
        },
      },
      {
        props: { variant: 'internal' },
        style: () => {
          return {
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            margin: 0,
          }
        },
      },
      {
        props: { variant: 'variant20' },
        style: () => {
          return {
            padding: 2,
            boxShadow: '0 12px 10px rgba(0,0,0,0.16)',
            margin: 0,
          }
        },
      },
    ],
  },
}

export {
  themeCos,
  themeLesOfficinales,
  themeGcapPharma,
  themeCentraleMediprix,
  themePharmacologyStore,
  themeCentraleDpharma,
  themeStore,
  themeViatris,
  themeAelia,
  themeOutremer,
  themeHelloPharmacie,
  themeNormandiePharma,
  themeTrenteSeptDegres,
}

// ---------- Helpers: appliquer des variables CSS runtime à partir du thème MUI ----------
export type ApplyThemeCssVarsOptions = {
  typoGris?: {
    size?: string
    leading?: string
    weight?: number | string
    tracking?: string
    fontFamily?: string
  }
}
